import React from 'react';
import { Heart, Target, Eye, Award, Sprout, Rocket } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const values = [
  {
    icon: <Heart className="w-12 h-12 text-gold" />,
    title: 'Humanité',
    description: 'Derrière chaque CV, il y a une personne. Nous plaçons l\u2019écoute, la bienveillance et le respect au cœur de chaque rencontre.',
  },
  {
    icon: <Target className="w-12 h-12 text-gold" />,
    title: 'Pertinence',
    description: 'Nous ne cherchons pas à faire du volume. Nous visons juste. Chaque connexion est pensée pour durer, évoluer et créer de la valeur.',
  },
  {
    icon: <Eye className="w-12 h-12 text-gold" />,
    title: 'Transparence',
    description: 'Nous croyons en une communication claire, honnête et directe — avec nos clients comme avec nos candidats.',
  },
  {
    icon: <Award className="w-12 h-12 text-gold" />,
    title: 'Expertise',
    description: 'Nous maîtrisons les enjeux de votre secteur, notamment celui de l\u2019assurance de dommages. Notre savoir-faire est précis, notre regard est affûté.',
  },
  {
    icon: <Sprout className="w-12 h-12 text-gold" />,
    title: 'Éthique',
    description: 'Nous agissons avec intégrité, responsabilité et engagement. Parce que le recrutement et la formation sont aussi une question de confiance.',
  },
  {
    icon: <Rocket className="w-12 h-12 text-gold" />,
    title: 'Ambition partagée',
    description: 'Nous accompagnons les entreprises et les talents vers un avenir professionnel aligné, stimulant et porteur de sens.',
  },
];

const AboutPage = () => {
  return (
    <div className="pt-16" data-testid="about-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-[#1a1a1a] overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6">
            À propos
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            Ce qui nous guide, ce qui nous distingue
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl md:text-3xl text-gray-800 leading-relaxed" style={{ lineHeight: '2' }}>
            Chez Nexor Talents, nous croyons que le recrutement et la formation ne se résument pas à des compétences techniques. Ils reposent sur des convictions profondes, des engagements clairs et une vision humaine du monde professionnel, où chaque talent est écouté et chaque compétence est élevée.
          </p>
        </div>
      </section>

      {/* Pillar 1: Exploration Audacieuse */}
      <section className="py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-16">
            <div className="md:w-2/5 flex justify-center">
              <img
                src="/images/mascots/nexi_solo.png?v=2"
                alt="Nexi scrute l'horizon avec sa loupe"
                className="w-full max-w-xs"
              />
            </div>
            <div className="md:w-3/5">
              <h3 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-3">
                Exploration Audacieuse
              </h3>
              <p className="text-gold font-semibold text-lg mb-6">Portée par Nexi</p>
              <p className="text-xl text-gray-700 leading-relaxed" style={{ lineHeight: '1.9' }}>
                Comme Nexi avec sa loupe, nous croyons qu'un bon recrutement exige de la curiosité. Nous explorons au-delà des évidences pour dénicher les talents rares et les opportunités cachées.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pillar 2: Sagesse Partagée */}
      <section className="py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row-reverse items-center gap-16">
            <div className="md:w-2/5 flex justify-center">
              <img
                src="/images/mascots/elyor_solo.png?v=2"
                alt="Élyor partage son savoir avec son livre doré"
                className="w-full max-w-xs"
              />
            </div>
            <div className="md:w-3/5">
              <h3 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-3">
                Sagesse Partagée
              </h3>
              <p className="text-gold font-semibold text-lg mb-6">Incarnée par Élyor</p>
              <p className="text-xl text-gray-700 leading-relaxed" style={{ lineHeight: '1.9' }}>
                Comme Élyor avec son grimoire, nous croyons en la transmission du savoir. Nos formations sont le fruit d'une expérience profonde destinée à élever les compétences de chacun.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pillar 3: Complicité Stratégique */}
      <section className="py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-16">
            <div className="md:w-2/5 flex justify-center">
              <img
                src="/images/mascots/complicite.png"
                alt="Nexi et Élyor, complices et unis"
                className="w-full max-w-xs"
              />
            </div>
            <div className="md:w-3/5">
              <h3 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-3">
                Complicité Stratégique
              </h3>
              <p className="text-gold font-semibold text-lg mb-6">L'union de Nexi et Élyor</p>
              <p className="text-xl text-gray-700 leading-relaxed" style={{ lineHeight: '1.9' }}>
                L'interaction entre Nexi et Élyor symbolise notre rôle de partenaire alliant l'agilité de la chasse de talents à la rigueur de la formation d'élite.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-20 text-center">
            Nos valeurs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {values.map((value) => (
              <Card key={value.title} className="border-0 shadow-xl hover:shadow-2xl bg-white">
                <CardContent className="p-10">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/10 mb-6">
                    {value.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Ambassadors */}
      <section className="py-28 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            <div className="text-center">
              <div className="inline-block bg-gold/10 rounded-2xl p-8 mb-8">
                <h3 className="text-3xl font-display font-bold text-gray-900 mb-2">Nexi</h3>
                <p className="text-gold font-semibold text-lg">L'Explorateur</p>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                Toujours en mouvement, loupe en main, il cherche la perle rare avec enthousiasme et détermination.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-block bg-gold/10 rounded-2xl p-8 mb-8">
                <h3 className="text-3xl font-display font-bold text-gray-900 mb-2">Élyor</h3>
                <p className="text-gold font-semibold text-lg">Le Sage doré</p>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                Bienveillant et posé, il guide avec sagesse et partage son savoir à travers son livre lumineux.
              </p>
            </div>
          </div>

          <div className="mt-20 text-center">
            <p className="text-2xl md:text-3xl text-gray-800 leading-relaxed font-light" style={{ lineHeight: '2' }}>
              Ensemble, ils <span className="text-gold font-semibold">veillent sur votre succès</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-28 bg-[#1a1a1a] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-10 text-gold">
            Ces valeurs ne sont pas des mots
          </h2>
          <p className="text-2xl text-gray-300 mb-8 leading-relaxed">
            Elles sont notre boussole.
          </p>
          <p className="text-xl text-gray-300 leading-relaxed">
            Elles nous permettent de bâtir des ponts solides entre les aspirations humaines et les besoins stratégiques.
          </p>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
